import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CremeLogo from './CremeLogo';
import { INSTAGRAM_URL } from '../config/site';

/* ── Menu data ────────────────────────────────────────────── */

interface MenuItem {
  label: string;
  path: string;
  children?: MenuItem[];
}

interface MenuCategory {
  label: string;
  path: string;
  children: MenuItem[];
}

const MENU_DATA: MenuCategory[] = [
  { label: 'How it works', path: '#how-it-works', children: [] },
  { label: 'Flavors', path: '#flavors', children: [] },
  { label: 'Gallery', path: '#gallery', children: [] },
  { label: 'Reviews', path: '#reviews', children: [] },
  { label: 'Order', path: '#order', children: [] },
];

/* ── Shared icons ─────────────────────────────────────────── */

function SearchIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={`shrink-0 ${className}`}
    >
      <circle cx="9" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.67" fill="none" />
      <path d="M14 14l4 4" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" />
    </svg>
  );
}

function AccountIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={`shrink-0 ${className}`}
    >
      <circle cx="10" cy="6" r="3.33" stroke="currentColor" strokeWidth="1.67" fill="none" />
      <path
        d="M3.33 18.33c0-3.68 2.99-6.66 6.67-6.66s6.67 2.98 6.67 6.66"
        stroke="currentColor"
        strokeWidth="1.67"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0">
      <path
        d="M6 12l4-4-4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BackArrow() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0">
      <path
        d="M12 4l-6 6 6 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0">
      <path
        d="M15 5L5 15M5 5l10 10"
        stroke="currentColor"
        strokeWidth="1.67"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ── Placeholder thumbnail ────────────────────────────────── */

function Thumbnail() {
  return (
    <div className="w-16 h-16 shrink-0 bg-card-bg flex items-center justify-center overflow-hidden">
      <span className="text-[8px] text-text-muted tracking-widest uppercase">IMG</span>
    </div>
  );
}

/* ── Desktop dropdown ─────────────────────────────────────── */

interface DesktopDropdownProps {
  category: MenuCategory;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

function DesktopDropdown({ category, isOpen, onOpen, onClose }: DesktopDropdownProps) {
  if (category.children.length === 0) {
    return (
      <a
        href={category.path}
        className="font-bar text-[9px] tracking-[0.2em] uppercase text-theme-font no-underline hover:opacity-80 transition-opacity font-normal"
      >
        {category.label}
      </a>
    );
  }

  return (
    <div className="relative" onMouseEnter={onOpen} onMouseLeave={onClose}>
      <button
        type="button"
        className="font-bar text-[9px] tracking-[0.2em] uppercase text-theme-font bg-transparent border-0 cursor-pointer hover:opacity-80 transition-opacity font-normal p-0"
        aria-expanded={isOpen}
      >
        {category.label}
      </button>

      <div
        className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 z-50 transition-all duration-200 ${
          isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
        }`}
      >
        <div className="bg-white shadow-lg border border-gray-100 min-w-[220px] py-3">
          {category.children.map((item) => (
            <Link
              key={item.path + item.label}
              to={item.path}
              className="block px-6 py-2 font-inter text-[11px] tracking-[0.15em] uppercase text-text-primary no-underline hover:bg-cream transition-colors"
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Mobile menu ──────────────────────────────────────────── */

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const navigate = useNavigate();
  const [menuStack, setMenuStack] = useState<{ title: string; items: MenuItem[] }[]>([]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setMenuStack([]);
    onClose();
  }, [onClose]);

  const handleCategoryClick = useCallback(
    (category: MenuCategory) => {
      if (category.children.length === 0) {
        handleClose();
        if (category.path.startsWith('#')) {
          setTimeout(() => {
            document.querySelector(category.path)?.scrollIntoView({ behavior: 'smooth' });
          }, 50);
        } else {
          navigate(category.path);
        }
        return;
      }
      setMenuStack([{ title: category.label, items: category.children }]);
    },
    [navigate, handleClose],
  );

  const handleItemClick = useCallback(
    (item: MenuItem) => {
      if (item.children && item.children.length > 0) {
        setMenuStack((prev) => [...prev, { title: item.label, items: item.children! }]);
        return;
      }
      handleClose();
      navigate(item.path);
    },
    [navigate, handleClose],
  );

  const handleBack = useCallback(() => {
    setMenuStack((prev) => prev.slice(0, -1));
  }, []);

  const currentLevel = menuStack.length > 0 ? menuStack[menuStack.length - 1] : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Main menu"
      className={`fixed inset-0 z-[1001] md:hidden transition-opacity duration-300 ${
        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
    >
      <div className="absolute inset-0 bg-white" aria-hidden />
      <div className="relative flex flex-col h-full">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 h-14 bg-sage shrink-0">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close menu"
              className="w-10 h-10 flex items-center justify-center border-0 bg-transparent cursor-pointer p-0"
            >
              <CloseIcon />
            </button>
            <button
              type="button"
              aria-label="Browse flavors"
              onClick={() => {
                handleClose();
                setTimeout(() => {
                  document.querySelector('#flavors')?.scrollIntoView({ behavior: 'smooth' });
                }, 50);
              }}
              className="w-10 h-10 flex items-center justify-center border-0 bg-transparent cursor-pointer p-0"
            >
              <SearchIcon />
            </button>
          </div>
          <Link to="/" onClick={handleClose} className="text-theme-font no-underline" aria-label="CRÈME – Home">
            <CremeLogo className="h-7 w-auto" />
          </Link>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Message us on Instagram"
            className="w-10 h-10 flex items-center justify-center border-0 bg-transparent cursor-pointer p-0 text-theme-font"
          >
            <AccountIcon />
          </a>
        </div>

        {/* Submenu header */}
        {currentLevel && (
          <div className="flex items-center gap-2 px-4 h-12 border-b border-gray-200 bg-white shrink-0">
            <button
              type="button"
              onClick={handleBack}
              className="w-8 h-8 flex items-center justify-center border-0 bg-transparent cursor-pointer p-0 text-text-primary"
              aria-label="Back"
            >
              <BackArrow />
            </button>
            <span className="font-inter text-xs font-medium tracking-[0.2em] uppercase text-text-primary">
              {currentLevel.title}
            </span>
          </div>
        )}

        {/* Menu body */}
        <div className="flex-1 overflow-auto bg-white">
          {!currentLevel
            ? MENU_DATA.map((cat, i) => (
                <div key={cat.label}>
                  <button
                    type="button"
                    onClick={() => handleCategoryClick(cat)}
                    className="w-full flex items-center gap-4 px-4 py-5 text-left border-0 bg-transparent cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <Thumbnail />
                    <span className="flex-1 font-inter text-xs font-medium tracking-[0.2em] uppercase text-text-primary">
                      {cat.label}
                    </span>
                    {cat.children.length > 0 && <ChevronRight />}
                  </button>
                  {i < MENU_DATA.length - 1 && <hr className="border-0 border-t border-gray-200 mx-4" />}
                </div>
              ))
            : currentLevel.items.map((item, i) => (
                <div key={item.label + item.path}>
                  <button
                    type="button"
                    onClick={() => handleItemClick(item)}
                    className="w-full flex items-center gap-4 px-4 py-5 text-left border-0 bg-transparent cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <Thumbnail />
                    <span className="flex-1 font-inter text-xs font-medium tracking-[0.2em] uppercase text-text-primary">
                      {item.label}
                    </span>
                    {item.children && item.children.length > 0 && <ChevronRight />}
                  </button>
                  {i < currentLevel.items.length - 1 && (
                    <hr className="border-0 border-t border-gray-200 mx-4" />
                  )}
                </div>
              ))}
        </div>
      </div>
    </div>
  );
}

/* ── Exports ──────────────────────────────────────────────── */

export { MobileMenu, DesktopDropdown, MENU_DATA, SearchIcon, AccountIcon, CloseIcon };
export type { MenuCategory, MenuItem };

export default function MegaMenuWrapper({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
